import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Switch, Text } from 'onefaceui-react-components';
import { useDispatch } from 'react-redux';
import { getResourceTo } from 'react/utils/utils';
import telematics from 'react/api/actions/telematics';
import {
  getPositionSourceName,
  getPositionSourceNameForTrackCompany
} from 'react/utils/coordinateUtils';
import { getChartData, MAP_TYPE_RANGE } from 'react/utils/chartUtils';
import {
  mergeInputSourcesWithCapabilities,
  TELEMETICS_DOMAINS,
  getTelematicsStorageDetails
} from 'react/utils/telematicsUtils';
import {
  InputSourceNameContainer,
  InputSourceTelematicItem,
  InputSourceContainer,
  TelematicsMultiSourceContainer,
  InputSourceTelematicIdentifier,
  TelematicsMultiSourceListContainer,
  TelematicsMultiSourceChartContainer,
  ChartWrapper
} from './TelematicsMultisource.styles';
import Chart from './Chart';
import { TelemeticEmptyState } from './TelemeticEmptyState';


function TelematicsMultiSource({ sources, capabilitiesFromTrackCompany, sessionData }) {
  const { ROUTE_DETAILS, TRACK_EQUIPMENTS } = TELEMETICS_DOMAINS;
  const id = sessionData?.route || sessionData?.equipment?.id || null;
  const domain = sessionData?.equipment?.id ? TRACK_EQUIPMENTS : ROUTE_DETAILS;
  const telematicsStoragedData = getTelematicsStorageDetails();
  const chartsRangeSelectedFilter = telematicsStoragedData?.charts?.[domain]?.[id] || {};
  const dispatch = useDispatch();
  const [toggles, setToggles] = React.useState(() => {
    const { toggles = {} } =
      telematicsStoragedData?.toggles?.[domain]?.[id] || {};
    if (toggles && Object.keys(toggles).length !== 0) {
      return toggles;
    }
    const initial = {};
    const sourcesWithCapabilities = mergeInputSourcesWithCapabilities(
      sources,
      capabilitiesFromTrackCompany
    );
    sourcesWithCapabilities?.forEach(({ source, capabilities }) => {
      initial[source] = {};
      capabilities.forEach(({ id }) => {
        initial[source][id] = true;
      });
    });
    return initial;
  });

  const zoomButtonsClick = React.useCallback(
    (e) => {
      if (!e.trigger) return;
      const {
        trigger,
      } = e;
      e.preventDefault();
      if (trigger === 'rangeSelectorButton') {
        const type = e.rangeSelectorButton.type;
        const capabilityName = e.target.series[0]?.options?.yAxis;
        const rangeIndex = MAP_TYPE_RANGE[type];
        const payload = telematics.actionCreators.setTelematicsRangeIndex({
          id,
          domain,
          capabilityName,
          rangeIndex
        });
        dispatch(
          payload
        );
      }
    },
    [id, dispatch]
  );

  if (!sources || sources.length === 0) {
    return <TelemeticEmptyState />;
  }

  const sourcesWithCapabilities = mergeInputSourcesWithCapabilities(
    sources,
    capabilitiesFromTrackCompany
  );

  const filteredSources = sourcesWithCapabilities.map((source) => ({
    ...source,
    capabilities: source.capabilities.filter(
      (cap) => toggles[source.source]?.[cap.id]
    )
  }));

  const chartData = getChartData(
    filteredSources,
    capabilitiesFromTrackCompany,
    sessionData,
    (e) => zoomButtonsClick(e)
  ).sort((a, b) =>
    a.yAxis.custom.capabilityId < b.yAxis.custom.capabilityId ? -1 : 1
  );
  const handleToggle = (sourceId, capId) => {
    const newToggle = {
      ...toggles,
      [sourceId]: {
        ...toggles[sourceId],
        [capId]: !toggles[sourceId][capId]
      }
    };
    setToggles(() => newToggle);
    dispatch(
      telematics.actionCreators.setTelematicsToggle({
        domain,
        id,
        toggles: newToggle
      })
    );
  };

  return (
    <TelematicsMultiSourceContainer>
      <TelematicsMultiSourceListContainer
        id="telematicList"
        flexDirection="column"
      >
        <h3 data-testid="telematic-title" style={{ marginBottom: 16 }}>
          {getResourceTo('Greenmile.Telematic.title')}
        </h3>
        {sourcesWithCapabilities.map((source) => (
          <InputSourceContainer key={source.source} flexDirection="column">
            {source.source !== 'UNKNOWN_SOURCE' && (
              <InputSourceNameContainer>
                <Text size="xs">
                  {getResourceTo('Greenmile.Telematic.sourceName')}
                </Text>
                <Flex flexDirection="row" alignItems="center" gap="1">
                  <InputSourceTelematicIdentifier color={source.color} />
                  <Text key={source.id} size="xs">
                    {source.sourceType === 'MOBILE'
                      ? getPositionSourceName(source)
                      : getPositionSourceNameForTrackCompany(source)}
                  </Text>
                </Flex>
              </InputSourceNameContainer>
            )}
            {source.capabilities.map((telematic) => (
              <InputSourceTelematicItem key={telematic.id}>
                <Text size="xs">
                  {getResourceTo(`${telematic.description}`)}
                </Text>
                <Switch
                  style={{ padding: '0' }}
                  id={`telematic-switch-${telematic.id}`}
                  checked={toggles[source.source]?.[telematic.id]}
                  onChange={() => handleToggle(source.source, telematic.id)}
                  switchWidth={42}
                  switchHeight={20}
                  switchKnobSize={12}
                />
              </InputSourceTelematicItem>
            ))}
          </InputSourceContainer>
        ))}
      </TelematicsMultiSourceListContainer>
      <TelematicsMultiSourceChartContainer id="graphicList">
        {chartData.map((data) => (
          <ChartWrapper key={data.yAxis.id}>
            <Chart
              data={data}
              rangeSelectorSelected={
                chartsRangeSelectedFilter[data.yAxis.id]?.rangeIndex
              }
            />
          </ChartWrapper>
        ))}
      </TelematicsMultiSourceChartContainer>
    </TelematicsMultiSourceContainer>
  );
}

const positionShape = PropTypes.shape({
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired
});

const telematicsListShape = PropTypes.objectOf(
  PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.number.isRequired,
      position: positionShape.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired
    })
  )
);

TelematicsMultiSource.propTypes = {
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      source: PropTypes.string,
      telematicsList: telematicsListShape
    })
  ).isRequired,
  capabilitiesFromTrackCompany: PropTypes.arrayOf(PropTypes.object).isRequired,
  sessionData: PropTypes.object.isRequired
};

export default TelematicsMultiSource;
